import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Device, LiveGpsUpdate } from "../api";
import { usePositionHistory } from "../hooks/usePositionHistory";
import { gpsPoint, gpsUiStatus } from "../lib/presence";
import {
  appendTrailPoint,
  durationForMoveMs,
  extendLiveTripTrail,
  formatGpsAge,
  formatHeading,
  formatSpeedKmh,
  haversineMeters,
  isUsableGpsFix,
  lerp,
  lerpHeading,
  mergeTrails,
  SNAP_DISTANCE_M,
  vehicleTrackLabel,
  vehicleTrackStatus,
  type TrailPoint,
  type VehicleTrackStatus,
} from "../lib/vehicle-track";

/** Empty-map fallback only — never used as a device position. */
const emptyMapCenter: [number, number] = [20, 0];

const CAR_SVG = `<svg class="vehicle-marker-car" viewBox="0 0 32 48" aria-hidden="true">
  <rect x="8" y="8" width="16" height="32" rx="5"/>
  <rect class="vehicle-marker-window" x="11" y="12" width="10" height="8" rx="1.5"/>
  <rect class="vehicle-marker-window" x="11" y="28" width="10" height="6" rx="1.5"/>
  <rect x="6" y="16" width="3" height="7" rx="1"/>
  <rect x="23" y="16" width="3" height="7" rx="1"/>
  <rect x="6" y="30" width="3" height="7" rx="1"/>
  <rect x="23" y="30" width="3" height="7" rx="1"/>
</svg>`;

function vehicleIcon(status: VehicleTrackStatus, selected: boolean): L.DivIcon {
  const size = selected ? 44 : 36;
  return new L.DivIcon({
    className: "vehicle-marker-wrap",
    html: `<span class="vehicle-marker vehicle-marker--${status}${selected ? " is-selected" : ""}"><span class="vehicle-marker-pulse"></span><span class="vehicle-marker-rot">${CAR_SVG}</span></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

const ICONS: Record<VehicleTrackStatus, { base: L.DivIcon; selected: L.DivIcon }> = {
  moving: { base: vehicleIcon("moving", false), selected: vehicleIcon("moving", true) },
  stopped: { base: vehicleIcon("stopped", false), selected: vehicleIcon("stopped", true) },
  offline: { base: vehicleIcon("offline", false), selected: vehicleIcon("offline", true) },
};

interface MarkerState {
  lat: number;
  lon: number;
  heading: number;
  fromLat: number;
  fromLon: number;
  fromHeading: number;
  targetLat: number;
  targetLon: number;
  targetHeading: number;
  startedAt: number;
  durationMs: number;
}

function MapEffects({
  points,
  followLat,
  followLon,
  follow,
  followLock,
  fitNonce,
  onUserPan,
}: {
  points: Array<[number, number]>;
  followLat: number;
  followLon: number;
  follow: boolean;
  followLock: boolean;
  fitNonce: number;
  onUserPan: () => void;
}) {
  const map = useMap();
  const fitted = useRef(false);
  const followPrimed = useRef(false);
  const lastFit = useRef(0);
  const lastFollowPan = useRef(0);

  useMapEvents({
    dragstart() {
      if (!followLock) onUserPan();
    },
  });

  useEffect(() => {
    if (fitted.current || points.length === 0) return;
    fitted.current = true;
    if (points.length === 1) {
      map.setView(points[0]!, 15);
      return;
    }
    map.fitBounds(points, { padding: [48, 48], maxZoom: 16 });
  }, [map, points]);

  useEffect(() => {
    if (!fitNonce || fitNonce === lastFit.current || points.length === 0) return;
    lastFit.current = fitNonce;
    if (points.length === 1) {
      map.setView(points[0]!, Math.max(map.getZoom(), 14), { animate: true });
      return;
    }
    map.fitBounds(points, { padding: [48, 48], maxZoom: 16, animate: true });
  }, [fitNonce, map, points]);

  useEffect(() => {
    if (!follow) {
      followPrimed.current = false;
      return;
    }
    if (!followPrimed.current) {
      followPrimed.current = true;
      lastFollowPan.current = performance.now();
      map.setView([followLat, followLon], Math.max(map.getZoom(), 16), { animate: true });
      return;
    }
    const nowMs = performance.now();
    if (nowMs - lastFollowPan.current < 280) return;
    lastFollowPan.current = nowMs;
    map.panTo([followLat, followLon], { animate: true, duration: 0.4 });
  }, [follow, followLat, followLon, map]);

  return null;
}

function VehicleMarker({
  position,
  heading,
  headingKnown,
  status,
  selected,
  zIndexOffset,
  onClick,
  children,
}: {
  position: [number, number];
  heading: number;
  headingKnown: boolean;
  status: VehicleTrackStatus;
  selected: boolean;
  zIndexOffset: number;
  onClick: () => void;
  children: ReactNode;
}) {
  const markerRef = useRef<L.Marker | null>(null);
  const icon = selected ? ICONS[status].selected : ICONS[status].base;

  useEffect(() => {
    if (!headingKnown) return;
    const apply = () => {
      const rot = markerRef.current?.getElement()?.querySelector<HTMLElement>(".vehicle-marker-rot");
      if (rot) rot.style.transform = `rotate(${Math.round(heading)}deg)`;
    };
    apply();
    const id = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(id);
  }, [heading, headingKnown, icon]);

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={icon}
      zIndexOffset={zIndexOffset}
      eventHandlers={{ click: onClick }}
    >
      {children}
    </Marker>
  );
}

function seedState(lat: number, lon: number, heading: number): MarkerState {
  return {
    lat,
    lon,
    heading,
    fromLat: lat,
    fromLon: lon,
    fromHeading: heading,
    targetLat: lat,
    targetLon: lon,
    targetHeading: heading,
    startedAt: 0,
    durationMs: 0,
  };
}

export interface PlaybackMapFix {
  deviceId: string;
  lat: number;
  lon: number;
  heading: number | null;
  speedKmh: number | null;
  recordedAt: string;
  gap: boolean;
  trail: Array<[number, number]>;
}

export function MapPanel({
  devices,
  live,
  selectedId,
  onSelect,
  gpsSocketConnected,
  playbackFix = null,
  liveTripId = null,
}: {
  devices: Device[];
  live: Record<string, LiveGpsUpdate>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  gpsSocketConnected: boolean;
  playbackFix?: PlaybackMapFix | null;
  /** When set, the map follows only this vehicle and draws the path since follow started. */
  liveTripId?: string | null;
}) {
  const anim = useRef<Record<string, MarkerState>>({});
  const liveTrails = useRef<Record<string, TrailPoint[]>>({});
  const tripTrail = useRef<TrailPoint[]>([]);
  const tripFor = useRef<string | null>(null);
  const headingKnown = useRef<Record<string, boolean>>({});
  const tripGpsOk = useRef(false);
  const liveTripRef = useRef<string | null>(null);
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [follow, setFollow] = useState(false);
  const [fitNonce, setFitNonce] = useState(0);
  const [tripGpsDown, setTripGpsDown] = useState(false);
  const tripActiveId = playbackFix ? null : liveTripId;
  liveTripRef.current = tripActiveId;
  const { points: historyPoints } = usePositionHistory(tripActiveId ? null : selectedId);

  useEffect(() => {
    if (!selectedId) setFollow(false);
  }, [selectedId]);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (playbackFix) setFollow(true);
  }, [playbackFix?.deviceId]);

  useEffect(() => {
    if (tripActiveId) {
      setFollow(true);
      if (tripFor.current !== tripActiveId) {
        tripTrail.current = [];
        tripFor.current = tripActiveId;
      }
      return;
    }
    if (tripFor.current) {
      tripTrail.current = [];
      tripFor.current = null;
      tripGpsOk.current = false;
      setTripGpsDown(false);
      setFollow(false);
      setFitNonce((n) => n + 1);
    }
  }, [tripActiveId]);

  useEffect(() => {
    for (const d of devices) {
      const pb = playbackFix && playbackFix.deviceId === d.id ? playbackFix : null;
      const livePos = live[d.id];
      const tripHold = Boolean(tripActiveId && d.id === tripActiveId && !pb && !isUsableGpsFix(livePos));
      const src = pb
        ? { lat: pb.lat, lon: pb.lon, heading_deg: pb.heading, speed_kmh: pb.speedKmh }
        : livePos ?? d.last_position;
      if (!src) continue;
      const heading = src.heading_deg != null && Number.isFinite(src.heading_deg) ? ((src.heading_deg % 360) + 360) % 360 : null;
      if (heading != null) headingKnown.current[d.id] = true;
      const cur = anim.current[d.id];
      if (!cur) {
        anim.current[d.id] = seedState(src.lat, src.lon, heading ?? 0);
        continue;
      }
      if (tripHold) continue;
      if (tripActiveId === d.id && !pb && isUsableGpsFix(livePos) && tripTrail.current.length === 0) {
        anim.current[d.id] = seedState(src.lat, src.lon, heading ?? cur.heading);
        tripTrail.current = extendLiveTripTrail([], { lat: src.lat, lon: src.lon }, true);
        continue;
      }
      const sameTarget =
        Math.abs(cur.targetLat - src.lat) < 1e-7 &&
        Math.abs(cur.targetLon - src.lon) < 1e-7 &&
        (heading == null || Math.abs(cur.targetHeading - heading) < 0.5);
      if (sameTarget) continue;
      const nextHeading = heading ?? cur.heading;
      const from = { lat: cur.lat, lon: cur.lon };
      const to = { lat: src.lat, lon: src.lon };
      const meters = haversineMeters(from, to);
      if (pb || meters >= SNAP_DISTANCE_M) {
        anim.current[d.id] = seedState(src.lat, src.lon, nextHeading);
        continue;
      }
      cur.fromLat = cur.lat;
      cur.fromLon = cur.lon;
      cur.fromHeading = cur.heading;
      cur.targetLat = src.lat;
      cur.targetLon = src.lon;
      cur.targetHeading = nextHeading;
      cur.startedAt = performance.now();
      cur.durationMs = durationForMoveMs(from, to, src.speed_kmh);
    }
    for (const [id, pos] of Object.entries(live)) {
      if (playbackFix?.deviceId === id) continue;
      if (!isUsableGpsFix(pos)) continue;
      liveTrails.current[id] = appendTrailPoint(liveTrails.current[id] ?? [], { lat: pos.lat, lon: pos.lon });
    }
    if (tripActiveId) {
      const ok = gpsSocketConnected && isUsableGpsFix(live[tripActiveId]);
      tripGpsOk.current = ok;
      setTripGpsDown((prev) => (prev === !ok ? prev : !ok));
    }
  }, [devices, live, playbackFix, tripActiveId, gpsSocketConnected]);

  useEffect(() => {
    let raf = 0;
    const step = (nowMs: number) => {
      let changed = false;
      for (const st of Object.values(anim.current)) {
        if (st.durationMs <= 0) {
          if (st.lat !== st.targetLat || st.lon !== st.targetLon || st.heading !== st.targetHeading) {
            st.lat = st.targetLat;
            st.lon = st.targetLon;
            st.heading = st.targetHeading;
            changed = true;
          }
          continue;
        }
        const t = Math.min(1, (nowMs - st.startedAt) / st.durationMs);
        const lat = lerp(st.fromLat, st.targetLat, t);
        const lon = lerp(st.fromLon, st.targetLon, t);
        const heading = lerpHeading(st.fromHeading, st.targetHeading, t);
        if (Math.abs(lat - st.lat) > 1e-8 || Math.abs(lon - st.lon) > 1e-8 || Math.abs(heading - st.heading) > 0.05) {
          st.lat = lat;
          st.lon = lon;
          st.heading = heading;
          changed = true;
        }
      }
      const tripId = liveTripRef.current;
      if (tripId && tripGpsOk.current) {
        const st = anim.current[tripId];
        if (st) {
          const next = extendLiveTripTrail(tripTrail.current, { lat: st.lat, lon: st.lon }, true);
          if (next !== tripTrail.current) {
            tripTrail.current = next;
            changed = true;
          }
        }
      }
      if (changed) setTick((n) => n + 1);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const visibleDevices = tripActiveId ? devices.filter((d) => d.id === tripActiveId) : devices;

  const points = useMemo(() => {
    const out: Array<[number, number]> = [];
    for (const d of visibleDevices) {
      const st = anim.current[d.id];
      const p = (playbackFix?.deviceId === d.id ? playbackFix : null) ?? live[d.id] ?? d.last_position;
      if (st) out.push([st.lat, st.lon]);
      else if (p) out.push([p.lat, p.lon]);
    }
    return out;
  }, [visibleDevices, live, tick, playbackFix]);

  const selectedTrail = useMemo(() => {
    if (tripActiveId) {
      return tripTrail.current.map((p) => [p.lat, p.lon] as [number, number]);
    }
    if (!selectedId) return [];
    if (playbackFix?.deviceId === selectedId && playbackFix.trail.length >= 2) return playbackFix.trail;
    const st = anim.current[selectedId];
    const head = st ? { lat: st.lat, lon: st.lon } : null;
    return mergeTrails(historyPoints, liveTrails.current[selectedId], head).map((p) => [p.lat, p.lon] as [number, number]);
  }, [selectedId, historyPoints, live, tick, playbackFix, tripActiveId]);

  const otherTrails = useMemo(() => {
    if (tripActiveId) return [];
    return devices
      .filter((d) => d.id !== selectedId)
      .map((d) => {
        const pts = liveTrails.current[d.id] ?? [];
        if (pts.length < 2) return null;
        return { id: d.id, positions: pts.map((p) => [p.lat, p.lon] as [number, number]) };
      })
      .filter((row): row is { id: string; positions: Array<[number, number]> } => row != null);
  }, [devices, selectedId, live, tick, tripActiveId]);

  const focusId = tripActiveId ?? selectedId;
  const selectedDevice = focusId ? devices.find((d) => d.id === focusId) ?? null : null;
  const selectedAnim = focusId ? anim.current[focusId] : undefined;
  const followLat = selectedAnim ? Number(selectedAnim.lat.toFixed(5)) : 0;
  const followLon = selectedAnim ? Number(selectedAnim.lon.toFixed(5)) : 0;

  return (
    <section className={`map-panel${tripActiveId ? " map-panel--live-trip" : ""}`} aria-label="Live GPS map">
      <MapContainer center={emptyMapCenter} zoom={3} className="map-root" scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapEffects
          points={points}
          followLat={followLat}
          followLon={followLon}
          follow={Boolean(follow && selectedAnim)}
          followLock={Boolean(tripActiveId)}
          fitNonce={fitNonce}
          onUserPan={() => {
            if (follow) setFollow(false);
          }}
        />
        {otherTrails.map((row) => (
          <Polyline
            key={`trail-${row.id}`}
            positions={row.positions}
            interactive={false}
            pathOptions={{ color: "#8b97b3", weight: 3, opacity: 0.45, lineJoin: "round", lineCap: "round" }}
          />
        ))}
        {selectedTrail.length >= 2 ? (
          <Polyline
            positions={selectedTrail}
            interactive={false}
            pathOptions={{ color: "#3d8bfd", weight: 5, opacity: 0.92, lineJoin: "round", lineCap: "round" }}
          />
        ) : null}
        {visibleDevices.map((d) => {
          const pb = playbackFix?.deviceId === d.id ? playbackFix : null;
          const st = anim.current[d.id];
          const fallback = pb ?? live[d.id] ?? d.last_position;
          if (!st && !fallback) return null;
          const lat = st?.lat ?? fallback!.lat;
          const lon = st?.lon ?? fallback!.lon;
          const livePos = live[d.id];
          const src = pb
            ? { speed_kmh: pb.speedKmh, heading_deg: pb.heading, recorded_at: pb.recordedAt }
            : livePos ?? d.last_position;
          const point = pb
            ? { lat: pb.lat, lon: pb.lon, recordedAt: pb.recordedAt, liveFix: !pb.gap }
            : gpsPoint(d, livePos);
          const gpsStatus = pb
            ? pb.gap
              ? "offline"
              : "live"
            : gpsUiStatus({
                deviceOnline: d.online,
                socketConnected: gpsSocketConnected,
                recordedAt: point?.recordedAt,
                liveFix: point?.liveFix,
                fixReport: point && "fixReport" in point ? point.fixReport : undefined,
              });
          const tripGap = Boolean(tripActiveId && tripGpsDown && d.id === tripActiveId);
          const gpsLive = gpsStatus === "live" && !tripGap;
          const speed = src?.speed_kmh ?? null;
          const heading = st?.heading ?? src?.heading_deg ?? null;
          const knownHeading = Boolean(headingKnown.current[d.id]);
          const motion = vehicleTrackStatus({ gpsLive: pb ? !pb.gap : gpsLive, speedKmh: speed });
          const selected = d.id === selectedId;
          const gpsLabel = pb
            ? pb.gap
              ? "Playback GPS gap"
              : "Playback GPS"
            : tripGap
              ? "GPS unavailable"
            : gpsStatus === "live"
              ? "GPS FIX"
              : gpsStatus === "reconnecting"
                ? "GPS reconnecting"
                : gpsStatus === "last_known"
                  ? "Last known"
                  : "NO GPS FIX";
          return (
            <VehicleMarker
              key={d.id}
              position={[lat, lon]}
              heading={knownHeading ? (heading ?? 0) : 0}
              headingKnown={knownHeading}
              status={motion}
              selected={selected}
              zIndexOffset={selected ? 1000 : motion === "offline" ? 0 : 250}
              onClick={() => onSelect(d.id)}
            >
              <Popup>
                <div className="map-popup">
                  <strong>{d.label || d.serial_no}</strong>
                  <div>
                    {vehicleTrackLabel(motion)}
                    {" · "}
                    {d.online ? "Camera online" : "Camera offline"}
                    {" · "}
                    {gpsLabel}
                  </div>
                  <div>
                    {formatSpeedKmh(speed)}
                    {" · "}
                    {formatHeading(knownHeading ? heading : null)}
                  </div>
                  <div>
                    {lat.toFixed(5)}, {lon.toFixed(5)}
                  </div>
                  <div>
                    {point?.recordedAt
                      ? `${pb ? "Recording" : gpsLive ? "Updated" : "Last updated"}: ${formatGpsAge(point.recordedAt, now)}`
                      : "No GPS fix yet"}
                  </div>
                </div>
              </Popup>
            </VehicleMarker>
          );
        })}
      </MapContainer>
      <div className="map-toolbar">
        <button
          type="button"
          className={follow ? "is-on" : ""}
          disabled={!selectedAnim}
          aria-pressed={follow}
          onClick={() => {
            if (tripActiveId) {
              setFollow(true);
              return;
            }
            setFollow((v) => !v);
          }}
        >
          {follow ? "Following" : "Follow"}
        </button>
        {tripActiveId ? null : (
          <button type="button" disabled={points.length === 0} onClick={() => { setFollow(false); setFitNonce((n) => n + 1); }}>
            Show all
          </button>
        )}
      </div>
      {tripActiveId ? (
        <div className={`map-live-trip${tripGpsDown ? " is-gap" : ""}`} role="status">
          {tripGpsDown ? "GPS unavailable — last known position" : "Live trip"}
        </div>
      ) : null}
      <div className="map-legend" aria-hidden="true">
        <span className="map-legend-item moving">Moving</span>
        <span className="map-legend-item stopped">Stopped</span>
        <span className="map-legend-item offline">Last known</span>
      </div>
      {selectedDevice && selectedAnim ? (
        <SelectedHud
          device={selectedDevice}
          live={live[selectedDevice.id]}
          lat={selectedAnim.lat}
          lon={selectedAnim.lon}
          heading={selectedAnim.heading}
          gpsSocketConnected={gpsSocketConnected}
          follow={follow}
          onFollowChange={(next) => setFollow(tripActiveId ? true : next)}
          now={now}
          gpsUnavailable={Boolean(tripActiveId && tripGpsDown)}
          playback={playbackFix?.deviceId === selectedDevice.id ? playbackFix : null}
        />
      ) : null}
    </section>
  );
}

function SelectedHud({
  device,
  live,
  lat,
  lon,
  heading,
  gpsSocketConnected,
  follow,
  onFollowChange,
  now,
  playback,
  gpsUnavailable = false,
}: {
  device: Device;
  live?: LiveGpsUpdate;
  lat: number;
  lon: number;
  heading: number;
  gpsSocketConnected: boolean;
  follow: boolean;
  onFollowChange: (next: boolean) => void;
  now: number;
  playback?: PlaybackMapFix | null;
  gpsUnavailable?: boolean;
}) {
  const src = playback
    ? { speed_kmh: playback.speedKmh, heading_deg: playback.heading, recorded_at: playback.recordedAt }
    : live ?? device.last_position;
  const point = playback
    ? { recordedAt: playback.recordedAt }
    : gpsPoint(device, live);
  const gpsStatus = playback
    ? playback.gap
      ? "offline"
      : "live"
    : gpsUiStatus({
        deviceOnline: device.online,
        socketConnected: gpsSocketConnected,
        recordedAt: point?.recordedAt,
        liveFix: point && "liveFix" in point ? point.liveFix : undefined,
        fixReport: point && "fixReport" in point ? point.fixReport : undefined,
      });
  const gpsLive = gpsStatus === "live" && !gpsUnavailable;
  const speed = src?.speed_kmh ?? null;
  const motion = vehicleTrackStatus({ gpsLive: playback ? !playback.gap : gpsLive, speedKmh: speed });
  const headingShown = gpsUnavailable
    ? live?.heading_deg != null && Number.isFinite(live.heading_deg)
      ? heading
      : src?.heading_deg
    : heading;
  const gpsLabel = playback
    ? playback.gap
      ? "Playback GPS gap"
      : "Playback GPS"
    : gpsUnavailable
      ? "GPS unavailable"
    : gpsStatus === "live"
      ? "GPS FIX"
      : gpsStatus === "reconnecting"
        ? "GPS reconnecting"
        : gpsStatus === "last_known"
          ? "Last known"
          : "NO GPS FIX";
  return (
    <aside className={`map-hud map-hud--${motion}`} aria-label="Selected vehicle">
      <div className="map-hud-top">
        <div className="map-hud-main">
          <strong>{device.label || device.serial_no}</strong>
          <span className={`map-hud-status map-hud-status--${motion}`}>{vehicleTrackLabel(motion)}</span>
        </div>
        <button type="button" className={follow ? "is-on" : ""} aria-pressed={follow} onClick={() => onFollowChange(!follow)}>
          {follow ? "Following" : "Follow"}
        </button>
      </div>
      <div className="map-hud-meta">
        <span>{formatSpeedKmh(speed)}</span>
        <span>{formatHeading(headingShown)}</span>
        <span>{gpsLabel}</span>
        <span>{point?.recordedAt ? formatGpsAge(point.recordedAt, now) : "No GPS fix yet"}</span>
      </div>
      <div className="map-hud-coords">
        {lat.toFixed(5)}, {lon.toFixed(5)}
      </div>
    </aside>
  );
}
